import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { profileService } from '../services/profileService';
import { ProfileSummary, ProfileDetail, AgentConfig } from '../types/profile';
import { LOADING_STATE } from '../types/constants';
import { FieldDefinition, FieldOption } from '../services/configService';

interface ProfileState {
  profilesList: ProfileSummary[];
  profilesListStatus: LOADING_STATE;
  currentProfile: ProfileDetail | null;
  currentProfileStatus: LOADING_STATE;
  editedFields: Record<string, Partial<FieldDefinition>>;
  editedPrompts: Record<string, Record<string, string>>;
  editedGlobalPrompt: string | null;
  hasUnsavedChanges: boolean;
}

const initialState: ProfileState = {
  profilesList: [],
  profilesListStatus: 'idle',
  currentProfile: null,
  currentProfileStatus: 'idle',
  editedFields: {},
  editedPrompts: {},
  editedGlobalPrompt: null,
  hasUnsavedChanges: false,
};

export const fetchProfilesList = createAsyncThunk(
  'profile/fetchList',
  async (_, { rejectWithValue }) => {
    try {
      const data = await profileService.fetchProfilesList();
      return data.profiles;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.detail || 'Failed to fetch profiles');
    }
  }
);

export const fetchProfile = createAsyncThunk(
  'profile/fetchDetail',
  async (profileId: string, { rejectWithValue }) => {
    try {
      return await profileService.fetchProfile(profileId);
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.detail || 'Failed to fetch profile');
    }
  }
);

export const saveField = createAsyncThunk(
  'profile/saveField',
  async ({ profileId, fieldId, fieldData }: { profileId: string; fieldId: string; fieldData: Record<string, any> }, { rejectWithValue }) => {
    try {
      return await profileService.updateField(profileId, fieldId, fieldData);
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.detail || 'Failed to save field');
    }
  }
);

export const createFieldAsync = createAsyncThunk(
  'profile/createField',
  async ({ profileId, fieldData }: { profileId: string; fieldData: Record<string, any> }, { rejectWithValue }) => {
    try {
      return await profileService.createField(profileId, fieldData);
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.detail || 'Failed to create field');
    }
  }
);

export const deleteFieldAsync = createAsyncThunk(
  'profile/deleteField',
  async ({ profileId, fieldId }: { profileId: string; fieldId: string }, { rejectWithValue }) => {
    try {
      await profileService.deleteField(profileId, fieldId);
      return fieldId;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.detail || 'Failed to delete field');
    }
  }
);

export const saveAgent = createAsyncThunk(
  'profile/saveAgent',
  async ({ profileId, agentId, agentData }: { profileId: string; agentId: string; agentData: Record<string, any> }, { rejectWithValue }) => {
    try {
      return await profileService.updateAgent(profileId, agentId, agentData);
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.detail || 'Failed to save agent');
    }
  }
);

export const createAgentAsync = createAsyncThunk(
  'profile/createAgent',
  async ({ profileId, agentData }: { profileId: string; agentData: Record<string, any> }, { rejectWithValue }) => {
    try {
      return await profileService.createAgent(profileId, agentData);
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.detail || 'Failed to create agent');
    }
  }
);

export const deleteAgentAsync = createAsyncThunk(
  'profile/deleteAgent',
  async ({ profileId, agentId }: { profileId: string; agentId: string }, { rejectWithValue }) => {
    try {
      await profileService.deleteAgent(profileId, agentId);
      return agentId;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.detail || 'Failed to delete agent');
    }
  }
);

export const seedProfileAsync = createAsyncThunk(
  'profile/seed',
  async (profileId: string, { rejectWithValue }) => {
    try {
      return await profileService.seedProfile(profileId);
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.detail || 'Failed to seed profile');
    }
  }
);

const profileSlice = createSlice({
  name: 'profile',
  initialState,
  reducers: {
    updateFieldEdit(state, action: PayloadAction<{ fieldName: string; changes: Partial<FieldDefinition> }>) {
      state.editedFields[action.payload.fieldName] = {
        ...state.editedFields[action.payload.fieldName],
        ...action.payload.changes,
      };
      state.hasUnsavedChanges = true;
    },
    updatePromptEdit(state, action: PayloadAction<{ agentId: string; promptKey: string; text: string }>) {
      if (!state.editedPrompts[action.payload.agentId]) {
        state.editedPrompts[action.payload.agentId] = {};
      }
      state.editedPrompts[action.payload.agentId][action.payload.promptKey] = action.payload.text;
      state.hasUnsavedChanges = true;
    },
    updateGlobalPromptEdit(state, action: PayloadAction<string>) {
      state.editedGlobalPrompt = action.payload;
      state.hasUnsavedChanges = true;
    },
    resetEdits(state) {
      state.editedFields = {};
      state.editedPrompts = {};
      state.editedGlobalPrompt = null;
      state.hasUnsavedChanges = false;
    },

    // Field property edits (mutates currentProfile directly)
    updateFieldProperty(state, action: PayloadAction<{ fieldName: string; key: string; value: any }>) {
      if (!state.currentProfile) return;
      const field = state.currentProfile.fields[action.payload.fieldName];
      if (field) {
        (field as any)[action.payload.key] = action.payload.value;
        state.hasUnsavedChanges = true;
      }
    },
    addFieldOption(state, action: PayloadAction<{ fieldName: string; option: FieldOption }>) {
      if (!state.currentProfile) return;
      const field = state.currentProfile.fields[action.payload.fieldName];
      if (field) {
        field.options.push(action.payload.option);
        state.hasUnsavedChanges = true;
      }
    },
    removeFieldOption(state, action: PayloadAction<{ fieldName: string; optionValue: string }>) {
      if (!state.currentProfile) return;
      const field = state.currentProfile.fields[action.payload.fieldName];
      if (field) {
        field.options = field.options.filter(o => o.value !== action.payload.optionValue);
        state.hasUnsavedChanges = true;
      }
    },
    changeFieldType(state, action: PayloadAction<{ fieldName: string; newType: string }>) {
      if (!state.currentProfile) return;
      const field = state.currentProfile.fields[action.payload.fieldName];
      if (field) {
        field.type = action.payload.newType as FieldDefinition['type'];
        if (action.payload.newType === 'text') {
          field.options = [];
        }
        state.hasUnsavedChanges = true;
      }
    },

    // Structural mutations
    addField(state, action: PayloadAction<{ agentId: string; field: FieldDefinition }>) {
      if (!state.currentProfile) return;
      const { agentId, field } = action.payload;
      state.currentProfile.fields[field.name] = field;
      const agent = state.currentProfile.agents.find(a => a.agent_id === agentId);
      if (agent) {
        agent.fields.push(field.name);
      }
      state.hasUnsavedChanges = true;
    },
    removeFieldFromAgent(state, action: PayloadAction<{ agentId: string; fieldName: string }>) {
      if (!state.currentProfile) return;
      const { agentId, fieldName } = action.payload;
      const agent = state.currentProfile.agents.find(a => a.agent_id === agentId);
      if (agent) {
        agent.fields = agent.fields.filter(f => f !== fieldName);
      }
      delete state.currentProfile.fields[fieldName];
      state.hasUnsavedChanges = true;
    },
    addAgent(state, action: PayloadAction<AgentConfig>) {
      if (!state.currentProfile) return;
      state.currentProfile.agents.push(action.payload);
      state.hasUnsavedChanges = true;
    },
    removeAgent(state, action: PayloadAction<string>) {
      if (!state.currentProfile) return;
      state.currentProfile.agents = state.currentProfile.agents.filter(
        a => a.agent_id !== action.payload
      );
      state.hasUnsavedChanges = true;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchProfilesList.pending, (state) => {
        state.profilesListStatus = 'pending';
      })
      .addCase(fetchProfilesList.fulfilled, (state, action: PayloadAction<ProfileSummary[]>) => {
        state.profilesListStatus = 'fulfilled';
        state.profilesList = action.payload;
      })
      .addCase(fetchProfilesList.rejected, (state) => {
        state.profilesListStatus = 'rejected';
      })
      .addCase(fetchProfile.pending, (state) => {
        state.currentProfileStatus = 'pending';
      })
      .addCase(fetchProfile.fulfilled, (state, action: PayloadAction<ProfileDetail>) => {
        state.currentProfileStatus = 'fulfilled';
        state.currentProfile = action.payload;
        state.editedFields = {};
        state.editedPrompts = {};
        state.editedGlobalPrompt = null;
        state.hasUnsavedChanges = false;
      })
      .addCase(fetchProfile.rejected, (state) => {
        state.currentProfileStatus = 'rejected';
      });
  },
});

export const {
  updateFieldEdit,
  updatePromptEdit,
  updateGlobalPromptEdit,
  resetEdits,
  updateFieldProperty,
  addFieldOption,
  removeFieldOption,
  changeFieldType,
  addField,
  removeFieldFromAgent,
  addAgent,
  removeAgent,
} = profileSlice.actions;
export default profileSlice.reducer;
